import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import VoiceTextInput from '../components/VoiceTextInput';
import StickmanVisualization from '../components/StickmanVisualization';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import { BackToTopButton } from '../components/NavigationHelpers';

const COUNTRIES = [
  'Australia', 'Canada', 'France', 'Germany', 'Ireland', 'Italy', 'Japan', 'Korea',
  'New Zealand', 'Portugal', 'Russia', 'Spain', 'Ukraine', 'United Kingdom',
  'United States', 'Other'
];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ko', name: 'Korean' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'zh', name: 'Mandarin (Simplified)' },
  { code: 'zh-TW', name: 'Mandarin (Traditional)' },
  { code: 'other', name: 'Other' }
];

const PRIMARY_DRUGS = [
  'Alcohol', 'Amphetamines', 'Cannabis', 'Cocaine', 'Heroin',
  'Methamphetamine (Ice)', 'Opioids (prescription)', 'Benzodiazepines',
  'MDMA/Ecstasy', 'Other'
];
const METHODS_OF_USE = ['Drinks', 'Ingests/Swallows', 'Smokes', 'Injects', 'Snorts/Inhales', 'Other'];
const INDUSTRIES = [
  'Agriculture', 'Construction', 'Education', 'Finance/Banking', 'Healthcare',
  'Hospitality/Tourism', 'Information Technology', 'Manufacturing', 'Mining',
  'Professional Services', 'Retail', 'Transport/Logistics', 'Government',
  'Arts/Entertainment', 'Other'
];
const TREATMENT_TYPES = ['Detoxification Service', 'Rehabilitation Service', 'Halfway House', 'Outpatient', 'AA/NA Meetings Only', 'Other'];
const MH_TREATMENT_TYPES = ['Inpatient', 'Outpatient', 'Psychiatrist', 'Psychologist', 'Counsellor', 'Other'];
const CHARGE_TYPES = ['Drug-related', 'Theft/Property', 'Violence/Assault', 'DUI/Traffic', 'Fraud/Financial', 'Family/Domestic', 'Other'];
const GENDER_OTHER_OPTIONS = ['Non-binary', 'Transgender', 'Gender fluid', 'Prefer not to say', 'Other'];

// Reusable components
function SectionHeader({ title, section, isOpen, onClick }) {
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between bg-blue-600 text-white px-4 py-3 rounded-lg font-bold uppercase tracking-wide" data-testid={`section-${section}`}>
      <span>{title}</span>
      {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
  );
}

function CheckboxOption({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-white hover:bg-blue-50 cursor-pointer transition-colors">
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

function YesNoSelect({ value, onChange }) {
  return (
    <div className="flex gap-3">
      {['yes', 'no'].map(v => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`flex-1 py-3 rounded-lg font-medium transition-colors ${value === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >{v === 'yes' ? 'Yes' : 'No'}</button>
      ))}
    </div>
  );
}

function SaveSectionButton({ sectionName, onSave }) {
  return (
    <Button type="button" onClick={() => onSave(sectionName)} className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white h-10 text-sm font-semibold" data-testid={`save-${sectionName.toLowerCase().replace(/\s/g, '-')}`}>
      <Check className="w-4 h-4 mr-2" /> Save {sectionName}
    </Button>
  );
}

// Module-scope SectionBlock — must NOT be defined inside PersonalHistoryPage,
// otherwise every formData state update creates a new component identity and
// unmounts every child input on every keystroke (causing the "only one letter
// typed" bug).  See: https://react.dev/learn/preserving-and-resetting-state
function SectionBlock({ num, title, sectionKey, isOpen, isIncomplete, onToggle, onSave, children }) {
  return (
    <div
      className={`bg-white rounded-xl shadow-lg overflow-hidden ${isIncomplete ? 'ring-2 ring-red-500' : ''}`}
      data-testid={`section-block-${sectionKey}`}
    >
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between text-white px-4 py-3 font-bold uppercase tracking-wide ${
          isIncomplete ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        data-testid={`section-${sectionKey}`}
      >
        <span className="flex items-center gap-2">
          {isIncomplete && <AlertTriangle className="w-4 h-4 text-amber-200" />}
          {num}. {title}
          {isIncomplete && (
            <span className="ml-1 text-[10px] font-bold bg-white text-red-700 px-2 py-0.5 rounded-full">
              INCOMPLETE
            </span>
          )}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && (
        <div className="p-4 space-y-4">
          {children}
          <SaveSectionButton sectionName={title} onSave={onSave} />
          {/* Bottom collapse chevron: lets the user close the section without
              scrolling all the way back to the top header. */}
          <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center justify-center gap-1.5 mt-2 py-2 text-xs font-semibold uppercase tracking-wide text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            data-testid={`section-collapse-${sectionKey}`}
          >
            <ChevronUp className="w-4 h-4" />
            Collapse Section
          </button>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children, requiredField, formData }) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-2">
      <span className="inline-flex items-center flex-wrap">
        {children}
        {requiredField && <RequiredFieldMarker field={requiredField} formData={formData} />}
      </span>
    </label>
  );
}

// Required-field metadata — MUST stay in sync with the backend `section_map`
// in /api/user/personal-history/status (server.py).  Used both for the live
// section-red-highlight derivation and the per-field "Required to progress"
// indicator.
const REQUIRED_FIELDS = [
  { field: 'gender',                          section: 'demographics',   label: 'Gender' },
  { field: 'age_group',                       section: 'demographics',   label: 'Age Group' },
  { field: 'crisis_why_now',                  section: 'crisis',         label: 'What brought you here now' },
  { field: 'crisis_last_drink_date',          section: 'crisis',         label: 'Last drink/drug date' },
  { field: 'primary_drug',                    section: 'drugUse',        label: 'Primary drug of choice' },
  { field: 'method_of_use',                   section: 'drugUse',        label: 'Method of use' },
  { field: 'ever_injected',                   section: 'drugUse',        label: 'Ever injected drugs?' },
  { field: 'living_arrangement',              section: 'living',         label: 'Current living arrangement' },
  { field: 'has_previous_treatment',          section: 'treatment',      label: 'Previous treatment?' },
  { field: 'has_mental_health_diagnosis',     section: 'mentalHealth',   label: 'Mental health diagnosis?' },
  { field: 'has_major_health_issues',         section: 'healthFitness',  label: 'Major health issues?' },
  { field: 'has_good_friends',                section: 'friends',        label: 'Have good friends?' },
  { field: 'has_legal_matters',               section: 'legal',          label: 'Legal matters?' },
];

const REQUIRED_FIELD_LOOKUP = REQUIRED_FIELDS.reduce((acc, r) => { acc[r.field] = r; return acc; }, {});

function isFieldFilled(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

// Small inline pill that flags a single required field as still-missing.
// Renders nothing once the field is filled — so it disappears the moment the
// user types/selects/saves.
function RequiredFieldMarker({ field, formData }) {
  const meta = REQUIRED_FIELD_LOOKUP[field];
  if (!meta) return null;
  if (isFieldFilled(formData[field])) return null;
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded-full border border-red-200"
      data-testid={`required-marker-${field}`}
    >
      <AlertTriangle className="w-3 h-3" />
      Required to progress
    </span>
  );
}

// Generic repeatable entries component (up to maxEntries)
function RepeatableEntries({ entries, onAdd, onUpdate, onRemove, maxEntries = 10, label, addLabel, renderEntry, colorClass = 'bg-gray-50 border-gray-200' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <FieldLabel>{label}</FieldLabel>
        {(entries || []).length < maxEntries && (
          <Button type="button" size="sm" variant="outline" onClick={onAdd}>
            <Plus className="w-4 h-4 mr-1" /> {addLabel}
          </Button>
        )}
      </div>
      {(entries || []).map((entry, index) => (
        <div key={index} className={`${colorClass} p-3 rounded-lg mb-3 border`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">{index === 0 ? 'Most Recent' : `Previous #${index}`}</span>
            <button onClick={() => onRemove(index)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
          {renderEntry(entry, index)}
        </div>
      ))}
      {(entries || []).length === 0 && (
        <p className="text-sm text-gray-500 italic">Click "{addLabel}" to add an entry</p>
      )}
    </div>
  );
}

// Case Management Plan — auto-generated from profile data, editable by user
function CaseManagementPlanSection({ formData, userName }) {
  const [isOpen, setIsOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');

  const generatePlan = () => {
    const d = formData;
    const sections = {};

    // Crisis Reminder (always first)
    if (d.crisis_why_now) {
      sections.crisis_reminder = `You came to the Axiom Recovery Programme because: "${d.crisis_why_now.substring(0, 300)}${d.crisis_why_now.length > 300 ? '...' : ''}"

Remember: the Disease will try to change your memory of what happened. It will minimise the crisis, rationalise the behaviour and convince you things "weren't that bad." They were. This is why you started. Hold on to this truth.`;
    } else {
      sections.crisis_reminder = 'Complete the Crisis section of your Personal History to generate your crisis reminder.';
    }

    // Sobriety Goal
    sections.sobriety = d.primary_drug
      ? `Maintain complete abstinence from ${d.primary_drug}${d.secondary_drugs?.length ? ` and ${d.secondary_drugs.join(', ')}` : ''}. Your sobriety clock started ${d.crisis_last_drink_date || 'when you made this commitment'}.`
      : 'Achieve and maintain sobriety from all substances.';

    // Diet Plan
    const dietItems = [];
    if (d.diet_regular_meals === 'no') dietItems.push('Establish regular meal times: breakfast, lunch and dinner');
    else dietItems.push('Continue eating regular meals: breakfast, lunch and dinner');
    if (d.diet_vegetarian === 'yes') dietItems.push('Maintain vegetarian/vegan diet with adequate protein sources (beans, lentils, tofu, nuts)');
    if (d.diet_food_allergies === 'yes') dietItems.push(`Avoid allergens: ${d.diet_allergy_details || 'as identified'}. Emergency plan: ${d.diet_allergy_action || 'seek medical attention'}`);
    if (d.diet_diabetic === 'yes') dietItems.push(`Manage diabetes (${d.diet_diabetic_type || 'type not specified'}) via ${d.diet_diabetic_management || 'treatment plan'}`);
    dietItems.push('Focus on whole foods, adequate hydration (2+ litres water daily) and reducing sugar/caffeine');
    dietItems.push('Budget tip: plan weekly meals, buy in bulk, cook at home: Recovery is also about nourishing your body');
    sections.diet = dietItems.join('\n');

    // Meetings
    sections.meetings = `Attend a MINIMUM of 5 meetings per week as required by the ARP programme. However, we strongly encourage MORE: aim for daily meetings in early Recovery.\n\nGet a HUMAN sponsor as soon as possible. Your AI Sponsor (Tudor) is available 24/7 but cannot replace the lived experience of another person in Recovery.\n\nMeeting attendance is not just a box to tick: it is where you hear your own story told by others and where hope is found.`;

    // Counselling
    sections.counselling = d.has_mental_health_diagnosis === 'yes'
      ? `Continue mental health treatment and medication compliance. ${d.medication_details ? `Current medications: ${d.medication_details}` : ''}\n\nConsider additional counselling or therapy focused on co-occurring disorders. Your mental health and your Recovery are deeply connected.`
      : 'Consider professional counselling to address underlying issues. Many people in Recovery benefit from therapy even without a formal diagnosis.';

    // Housing
    sections.housing = d.living_arrangement
      ? `Current: ${d.living_arrangement}${d.living_how_long ? ` (${d.living_how_long})` : ''}.\n\n${d.living_arrangement.includes('Homeless') ? 'PRIORITY: Secure stable housing. Contact local housing services and community support organisations. Stable housing is critical to sustained Recovery.' : 'Maintain stable housing. If your current arrangement is at risk, plan ahead: have a backup plan.'}\n\nLong-term goal: Secure safe, stable accommodation that supports your Recovery.`
      : 'Secure stable, supportive housing. Contact local housing services if needed.';

    // Employment
    const empItems = [];
    if (d.is_employed === 'yes') {
      empItems.push(`Currently employed: ${d.profession || ''} at ${d.employer || 'current employer'}`);
      if (d.work_environment_dangerous === 'yes') {
        empItems.push(`WARNING: Your current work environment has been identified as dangerous to your Recovery. ${d.work_environment_details || ''} Consider transitioning to a safer role or industry.`);
      }
    } else {
      empItems.push('Develop employment readiness. Update your CV, explore job training programmes and consider volunteer work as a stepping stone.');
    }
    if (d.lost_business_due_to_addiction === 'yes') empItems.push('You lost your business to addiction. This is part of your story, not the end of it. Focus on rebuilding step by step.');
    empItems.push('Long-term: Find or maintain employment in an environment that supports your Recovery. Avoid high-risk workplaces (bars, clubs, etc.).');
    sections.employment = empItems.join('\n\n');

    // Health & Fitness
    const healthItems = [];
    if (d.has_major_health_issues === 'yes') healthItems.push(`Address health issues: ${d.health_issue_details || 'as identified'}`);
    if (d.medication_allergies === 'yes') healthItems.push(`Medication allergies: ${d.medication_allergy_details || 'as reported'}`);
    if (d.chronic_conditions === 'yes') healthItems.push(`Continue chronic condition management: ${d.chronic_condition_details || 'as identified'}`);
    healthItems.push('Begin or maintain a regular exercise routine. Even 30 minutes of walking daily improves mental health and supports Recovery.');
    sections.health = healthItems.join('\n');

    // Friends & Social
    sections.social = d.friends_use_drugs === 'yes'
      ? `Be honest: your current social circle includes people who use drugs/alcohol. This is one of the biggest risks to your Recovery. You may need to distance yourself from using friends: at least in early Recovery. Build new friendships through meetings, Recovery groups and sober activities.`
      : 'Maintain positive friendships and build new connections through the Recovery community. Attend social events at meetings and Recovery groups.';

    // Legal
    if (d.has_legal_matters === 'yes' || d.crisis_legal_issue === 'yes') {
      sections.legal = `Active legal matters require your attention and compliance. ${d.axiom_assist_court === 'yes' || d.crisis_using_for_court === 'yes' ? 'Axiom RP will prepare progress reports for the Court upon completion of the programme. Your genuine engagement is essential: the Court will see through anything less.' : 'Keep your legal team informed of your programme progress.'}\n\nMaintain full compliance with all court orders and conditions.`;
    }

    // Long-term Beyond Programme
    sections.long_term = `Upon completing the Axiom Recovery Programme (a 32-week programme that starts with the 2-Week Window):\n\n1. Continue attending meetings regularly (3-5 per week minimum)\n2. Maintain your human sponsor relationship\n3. Consider service work: helping others in early Recovery\n4. Continue journaling and mental health check-ins\n5. Stay connected to the Recovery community\n6. Revisit this Case Management Plan quarterly\n7. Remember: Recovery is a lifelong journey, not a 32-week programme`;

    return sections;
  };

  const loadPlan = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/case-management-plan`);
      if (response.ok) {
        const data = await response.json();
        if (data.plan_sections && Object.keys(data.plan_sections).length > 0) {
          setPlan(data);
          return;
        }
      }
    } catch (e) { console.error(e); }
    // Generate fresh plan from form data
    const generated = generatePlan();
    setPlan({ plan_sections: generated, original_sections: generated, user_edits: {} });
  };

  const savePlan = async (sections) => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/case-management-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_sections: sections,
          original_sections: plan?.original_sections || sections,
          user_edits: plan?.user_edits || {}
        })
      });
    } catch (e) { console.error(e); }
  };

  const handleGenerateOrRefresh = async () => {
    const generated = generatePlan();
    const newPlan = { plan_sections: generated, original_sections: generated, user_edits: {} };
    setPlan(newPlan);
    await savePlan(generated);
    toast.success('Case Management Plan generated from your profile');
  };

  const handleEditSave = async (key) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/case-management-plan/edit-section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_key: key, content: editValue })
      });
      if (response.ok) {
        setPlan(prev => ({
          ...prev,
          plan_sections: { ...prev.plan_sections, [key]: editValue },
          user_edits: { ...prev.user_edits, [key]: { content: editValue, edited_at: new Date().toISOString(), edited_by: 'user' } }
        }));
        setEditingSection(null);
        toast.success('Section updated');
      }
    } catch (e) { toast.error('Failed to save edit'); }
  };

  // Load the saved plan when the modal opens.  We use a ref to ensure
  // exactly one load per modal-open event — otherwise depending on `plan`
  // here would loop, and depending on `loadPlan` (which closes over the
  // form data and `generatePlan`) would re-run on every keystroke.
  const planLoadedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      planLoadedRef.current = false;
      return;
    }
    if (planLoadedRef.current || plan) return;
    planLoadedRef.current = true;
    loadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot load per open
  }, [isOpen]);

  const PLAN_SECTIONS = [
    { key: 'crisis_reminder', title: 'Your Crisis: Why You Are Here', color: 'border-red-200 bg-red-50' },
    { key: 'sobriety', title: 'Sobriety Goal', color: 'border-blue-200 bg-blue-50' },
    { key: 'diet', title: 'Dietary Plan', color: 'border-green-200 bg-green-50' },
    { key: 'meetings', title: 'Meetings & Sponsorship', color: 'border-indigo-200 bg-indigo-50' },
    { key: 'counselling', title: 'Counselling & Mental Health', color: 'border-violet-200 bg-violet-50' },
    { key: 'housing', title: 'Housing & Accommodation', color: 'border-sky-200 bg-sky-50' },
    { key: 'employment', title: 'Employment & Career', color: 'border-amber-200 bg-amber-50' },
    { key: 'health', title: 'Health & Fitness', color: 'border-rose-200 bg-rose-50' },
    { key: 'social', title: 'Friends & Social Circle', color: 'border-teal-200 bg-teal-50' },
    { key: 'legal', title: 'Legal Matters', color: 'border-zinc-300 bg-zinc-50' },
    { key: 'long_term', title: 'Long-Term Plan (Beyond Programme)', color: 'border-emerald-200 bg-emerald-50' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="case-management-plan">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between bg-emerald-600 text-white px-4 py-3 rounded-lg font-bold uppercase tracking-wide" data-testid="section-casePlan">
        <span>12. Case Management Plan</span>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {isOpen && (
        <div className="p-4 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs text-emerald-800 leading-relaxed">
              This plan is auto-generated from your Personal History. It aims to be a <strong>positive, long-term support tool</strong> for your entire Recovery journey: a 32-week programme that starts with the 2-Week Window and beyond. Review each section for accuracy: you can edit any section and your changes will be tracked alongside the original.
            </p>
          </div>

          <div className="flex gap-2">
            <Button type="button" onClick={handleGenerateOrRefresh} variant="outline" className="text-xs h-9 flex-1" data-testid="generate-plan-btn">
              {plan ? 'Regenerate from Profile' : 'Generate Plan'}
            </Button>
          </div>

          {plan && Object.keys(plan.plan_sections || {}).length > 0 && (
            <div className="space-y-3">
              {PLAN_SECTIONS.map(({ key, title, color }) => {
                const content = plan.plan_sections?.[key];
                if (!content && key === 'legal' && formData.has_legal_matters !== 'yes' && formData.crisis_legal_issue !== 'yes') return null;
                const isEdited = plan.user_edits?.[key];
                const isEditing = editingSection === key;

                return (
                  <div key={key} className={`border rounded-xl p-3 ${color}`} data-testid={`plan-section-${key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{title}</h4>
                      {isEdited && !isEditing && (
                        <span className="text-[9px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-medium" data-testid={`edited-badge-${key}`}>
                          Edited by User &middot; {new Date(isEdited.edited_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} rows={5} className="text-xs" data-testid={`edit-textarea-${key}`} />
                        <div className="flex gap-2">
                          <Button type="button" onClick={() => handleEditSave(key)} className="bg-green-600 text-white text-xs h-8 flex-1" data-testid={`save-edit-${key}`}>Save</Button>
                          <Button type="button" onClick={() => setEditingSection(null)} variant="outline" className="text-xs h-8">Cancel</Button>
                        </div>
                        {isEdited && (
                          <p className="text-[10px] text-gray-400">Original: {plan.original_sections?.[key]?.substring(0, 100)}...</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{content || 'Not yet generated'}</p>
                        <button
                          onClick={() => { setEditingSection(key); setEditValue(content || ''); }}
                          className="text-[10px] text-blue-600 font-medium mt-2 hover:text-blue-700"
                          data-testid={`edit-btn-${key}`}
                        >
                          Edit this section
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full flex items-center justify-center gap-1.5 mt-4 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors"
            data-testid="section-collapse-casePlan"
          >
            <ChevronUp className="w-4 h-4" />
            Collapse Section
          </button>
        </div>
      )}
    </div>
  );
}

function PersonalHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { user, checkAuth } = useAuth();
  // Sections to highlight as incomplete (red) when navigated from the Dashboard
  // reminder.  Names match backend `missing_sections` from /api/user/personal-history/status.
  const incompleteSectionLabels = (location.state && location.state.incompleteSections) || [];
  // Map backend section labels -> internal sectionKey used by expandedSections
  const LABEL_TO_KEY = {
    'Demographics': 'demographics',
    'Crisis': 'crisis',
    'Drug Use': 'drugUse',
    'Living & Diet': 'living',
    'Treatment History': 'treatment',
    'Mental Health': 'mentalHealth',
    'Health & Fitness': 'healthFitness',
    'Friends': 'friends',
    'Legal Matters': 'legal',
  };
  // Initial highlight set — used ONLY to auto-expand the first time the user
  // lands on the page from the Dashboard reminder.  Live red-highlighting is
  // derived from `formData` further down so the colour fades automatically as
  // the user fills in the required fields.
  const initialIncompleteKeys = new Set(incompleteSectionLabels.map((l) => LABEL_TO_KEY[l]).filter(Boolean));
  // Auto-expand the first incomplete section so the user sees what is missing
  const [expandedSections, setExpandedSections] = useState(() => {
    if (initialIncompleteKeys.size > 0) {
      const out = {};
      for (const k of initialIncompleteKeys) out[k] = true;
      return out;
    }
    return { demographics: true };
  });

  const [formData, setFormData] = useState({
    // 1. Demographics
    gender: '', gender_other_type: '', gender_other_details: '',
    lgbtqi_identifies: '',  // 'yes' | 'no' | 'prefer_not_to_say': gates LGBTQI+ recovery room visibility
    age_group: '', indigenous_status: '', indigenous_details: '',
    country_of_birth: '', country_of_birth_other: '',
    preferred_language: '', preferred_language_other: '',
    other_languages: [], other_language_specify: '',
    
    // 2. Crisis
    crisis_why_now: '', crisis_why_not_sooner: '', crisis_know_axiom: '',
    crisis_heard_about: '', crisis_what_happened: '', crisis_legal_issue: '',
    crisis_using_for_court: '', crisis_lost_employment: '', crisis_lost_family: '',
    crisis_what_made_you_stop: '',
    crisis_last_drink_date: '', crisis_last_drink_time: '', crisis_last_drink_day: '',
    
    // 3. Drug Use
    primary_drug: '', secondary_drugs: [], method_of_use: [],
    ever_injected: '', injected_last_3_months: '', additional_drug_info: '',
    
    // 4. Living & Diet
    living_arrangement: '', homeless_details: '',
    usual_accommodation: '', usual_accommodation_other: '',
    living_how_long: '', living_how_long_stay: '',
    additional_living_info: '',
    diet_vegetarian: '', diet_specific_needs: '', diet_food_allergies: '',
    diet_allergy_details: '', diet_allergy_action: '', diet_regular_meals: '',
    diet_diabetic: '', diet_diabetic_type: '', diet_diabetic_management: '',
    
    // 5. Income & Employment
    is_employed: '', employer: '', profession: '', industry: '',
    employment_duration: '', years_in_industry: '',
    has_own_business: '', own_business_details: '', lost_business_due_to_addiction: '',
    work_environment_dangerous: '', work_environment_details: '',
    has_previous_employment: '', employment_history: [],
    income_details: '',
    
    // 6. Treatment (repeatable up to 10)
    has_previous_treatment: '', treatment_entries: [],
    additional_treatment_info: '',
    
    // 7. Mental Health (repeatable up to 10)
    has_mental_health_diagnosis: '', mh_admissions: [],
    currently_receiving_mh_treatment: '', medication_details: '',
    other_mh_treatments: '', additional_mh_info: '',
    
    // 8. Health & Fitness
    has_major_health_issues: '', health_issue_details: '',
    medication_allergies: '', medication_allergy_details: '',
    chronic_conditions: '', chronic_condition_details: '',
    
    // 9. Friends
    has_good_friends: '', friends_use_drugs: '', friends_use_amount: '',
    friends_details: '',
    family_drug_use: '', family_drug_details: '',
    
    // 10. Legal (repeatable up to 10)
    has_legal_matters: '', charge_types: [], legal_location: '',
    court_date_pending: '', axiom_assist_court: '', report_requested: '',
    previous_legal_history: '', previous_charges: [],
    legal_documents: [],
    additional_legal_info: ''
  });

  // Live-derived set of section keys with at least one missing required field.
  // Re-evaluates on every render so red highlights fade as the user fills
  // fields — no need to refresh status from the server.
  const incompleteKeys = new Set(
    REQUIRED_FIELDS.filter((r) => !isFieldFilled(formData[r.field])).map((r) => r.section)
  );

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      return { ...prev, [field]: current.includes(value) ? current.filter(v => v !== value) : [...current, value] };
    });
  };

  // Generic array helpers
  const addEntry = (field, template) => {
    setFormData(prev => ({ ...prev, [field]: [...(prev[field] || []), template] }));
  };
  const updateEntry = (field, index, key, value) => {
    setFormData(prev => {
      const arr = [...(prev[field] || [])];
      arr[index] = { ...arr[index], [key]: value };
      return { ...prev, [field]: arr };
    });
  };
  const removeEntry = (field, index) => {
    setFormData(prev => ({ ...prev, [field]: (prev[field] || []).filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (sectionName = null) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/personal-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success(sectionName ? `${sectionName} saved` : 'Personal history saved');
        // If Crisis section saved with sobriety date, refresh auth to update clock
        if (formData.crisis_last_drink_date) {
          await checkAuth();
        }
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  // Load existing data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/personal-history`);
        if (response.ok) {
          const data = await response.json();
          if (data && Object.keys(data).length > 0) {
            setFormData(prev => ({ ...prev, ...data }));
          }
        }
      } catch (error) { console.error(error); }
    };
    fetchData();
  }, []);

  const getIndigenousLabel = () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz.includes('Australia')) return 'Aboriginal and/or Torres Strait Islander / Indigenous';
    if (tz.includes('America') && tz.includes('Canada')) return 'First Nations, Metis, Inuit / Indigenous';
    if (tz.includes('America')) return 'Native American, Alaska Native / Indigenous';
    if (tz.includes('Pacific/Auckland')) return 'Maori, Pacific Islander / Indigenous';
    return 'Indigenous or First Nations';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader title="Personal History" />
      <BackToTopButton />

      <main className="pt-20 pb-6 px-4">
        <div className="max-w-2xl mx-auto space-y-4">

          {/* ===== 1. DEMOGRAPHICS ===== */}
          <SectionBlock num={1} title="Demographics" sectionKey="demographics" isOpen={expandedSections.demographics} isIncomplete={incompleteKeys.has("demographics")} onToggle={() => toggleSection("demographics")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="gender" formData={formData}>Gender</FieldLabel>
              <div className="grid grid-cols-3 gap-2">
                {['Male', 'Female', 'Other'].map(g => (
                  <CheckboxOption key={g} label={g} checked={formData.gender === g.toLowerCase()} onChange={() => handleChange('gender', formData.gender === g.toLowerCase() ? '' : g.toLowerCase())} />
                ))}
              </div>
              {formData.gender === 'other' && (
                <div className="mt-3 space-y-2">
                  <Select value={formData.gender_other_type} onValueChange={(v) => handleChange('gender_other_type', v)}>
                    <SelectTrigger><SelectValue placeholder="Please specify..." /></SelectTrigger>
                    <SelectContent>{GENDER_OTHER_OPTIONS.map(o => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                  </Select>
                  {formData.gender_other_type === 'Other' && (
                    <VoiceTextInput value={formData.gender_other_details} onChange={(v) => handleChange('gender_other_details', v)} placeholder="Please describe..." rows={2} testId="gender-other-details" />
                  )}
                </div>
              )}
            </div>

            {/* LGBTQI+ self-identification: gates access to the LGBTQI+ recovery room.
                Three options give a respectful "prefer not to say" path that does NOT
                grant room access (matches the privacy posture of the gendered rooms). */}
            <div data-testid="lgbtqi-question">
              <FieldLabel>Do you identify as part of the LGBTQI+ community?</FieldLabel>
              <p className="text-[11px] text-zinc-500 leading-relaxed mb-2">
                Answering "Yes" unlocks the LGBTQI+ Recovery Room: a space reserved for members of that community. Your answer is private and visible only to you.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: 'yes', l: 'Yes' },
                  { v: 'no', l: 'No' },
                  { v: 'prefer_not_to_say', l: 'Prefer not to say' },
                ].map((o) => (
                  <CheckboxOption
                    key={o.v}
                    label={o.l}
                    checked={formData.lgbtqi_identifies === o.v}
                    onChange={() => handleChange('lgbtqi_identifies', formData.lgbtqi_identifies === o.v ? '' : o.v)}
                  />
                ))}
              </div>
            </div>

            <div>
              <FieldLabel requiredField="age_group" formData={formData}>Age Group</FieldLabel>
              <div className="grid grid-cols-3 gap-2">
                {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map(age => (
                  <CheckboxOption key={age} label={age} checked={formData.age_group === age} onChange={() => handleChange('age_group', formData.age_group === age ? '' : age)} />
                ))}
              </div>
            </div>

            <div>
              <FieldLabel>{getIndigenousLabel()}</FieldLabel>
              <YesNoSelect value={formData.indigenous_status} onChange={(v) => handleChange('indigenous_status', v)} />
              {formData.indigenous_status === 'yes' && (
                <div className="mt-3">
                  <VoiceTextInput value={formData.indigenous_details} onChange={(v) => handleChange('indigenous_details', v)} placeholder="Describe your Indigenous heritage..." rows={2} testId="indigenous-details" />
                </div>
              )}
            </div>

            <div>
              <FieldLabel>Country of Birth</FieldLabel>
              <Select value={formData.country_of_birth} onValueChange={(v) => handleChange('country_of_birth', v)}>
                <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
              </Select>
              {formData.country_of_birth === 'Other' && (
                <div className="mt-3">
                  <VoiceTextInput value={formData.country_of_birth_other} onChange={(v) => handleChange('country_of_birth_other', v)} placeholder="Enter your country..." rows={1} testId="country-other" />
                </div>
              )}
            </div>

            <div>
              <FieldLabel>Preferred Language</FieldLabel>
              <Select value={formData.preferred_language} onValueChange={(v) => handleChange('preferred_language', v)}>
                <SelectTrigger><SelectValue placeholder="Select primary language" /></SelectTrigger>
                <SelectContent>{LANGUAGES.map(l => (<SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>))}</SelectContent>
              </Select>
              {formData.preferred_language === 'other' && (
                <div className="mt-2">
                  <VoiceTextInput value={formData.preferred_language_other} onChange={(v) => handleChange('preferred_language_other', v)} placeholder="Enter your preferred language..." rows={1} testId="lang-other" />
                </div>
              )}
            </div>

            <div>
              <FieldLabel>Other Languages Spoken</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                {LANGUAGES.filter(l => l.code !== formData.preferred_language).map(l => (
                  <CheckboxOption key={l.code} label={l.name} checked={(formData.other_languages || []).includes(l.code)} onChange={() => handleMultiSelect('other_languages', l.code)} />
                ))}
              </div>
              {(formData.other_languages || []).includes('other') && (
                <div className="mt-2">
                  <VoiceTextInput value={formData.other_language_specify} onChange={(v) => handleChange('other_language_specify', v)} placeholder="Specify other language(s)..." rows={1} testId="other-lang-specify" />
                </div>
              )}
            </div>
          </SectionBlock>

          {/* ===== 2. CRISIS ===== */}
          <SectionBlock num={2} title="Crisis" sectionKey="crisis" isOpen={expandedSections.crisis} isIncomplete={incompleteKeys.has("crisis")} onToggle={() => toggleSection("crisis")} onSave={handleSubmit}>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
              <p className="text-xs text-amber-800 leading-relaxed">This section helps us understand what brought you to the Axiom Recovery Programme and your current situation. Please be as honest as possible: your answers are confidential and will help us support you.</p>
            </div>

            <div>
              <FieldLabel>What happened that made you come to Axiom Recovery Programme now? Exactly why NOW?</FieldLabel>
              <VoiceTextInput value={formData.crisis_why_now} onChange={(v) => handleChange('crisis_why_now', v)} placeholder="Explain what brought you here right now..." rows={5} testId="crisis-why-now" />
            </div>

            <div>
              <FieldLabel>Why didn't you go into treatment or start Axiom Recovery Programme sooner?</FieldLabel>
              <VoiceTextInput value={formData.crisis_why_not_sooner} onChange={(v) => handleChange('crisis_why_not_sooner', v)} placeholder="What stopped you before..." rows={4} testId="crisis-why-not-sooner" />
            </div>

            <div>
              <FieldLabel>Did you know about Axiom before?</FieldLabel>
              <YesNoSelect value={formData.crisis_know_axiom} onChange={(v) => handleChange('crisis_know_axiom', v)} />
            </div>

            <div>
              <FieldLabel>Where did you hear about Axiom?</FieldLabel>
              <VoiceTextInput value={formData.crisis_heard_about} onChange={(v) => handleChange('crisis_heard_about', v)} placeholder="A friend, doctor, court, online..." rows={2} testId="crisis-heard-about" />
            </div>

            <div>
              <FieldLabel>Explain in detail what happened that led you here</FieldLabel>
              <VoiceTextInput value={formData.crisis_what_happened} onChange={(v) => handleChange('crisis_what_happened', v)} placeholder="Tell us your story in as much detail as you need..." rows={8} testId="crisis-what-happened" />
            </div>

            <div>
              <FieldLabel>Do you have a legal issue now?</FieldLabel>
              <YesNoSelect value={formData.crisis_legal_issue} onChange={(v) => handleChange('crisis_legal_issue', v)} />
            </div>

            <div>
              <FieldLabel>Are you planning on using Axiom Recovery Programme to provide the Court with material to assist with your sentencing?</FieldLabel>
              <YesNoSelect value={formData.crisis_using_for_court} onChange={(v) => handleChange('crisis_using_for_court', v)} />
              {formData.crisis_using_for_court === 'yes' && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mt-3">
                  <p className="text-xs text-blue-900 leading-relaxed font-medium">
                    <strong>Important:</strong> Axiom Recovery Programme does not believe incarceration is a useful or successful process to help a person with alcohol or drug problems. However, it is very important that you are serious about ALL aspects of Axiom RP.
                  </p>
                  <p className="text-xs text-blue-800 leading-relaxed mt-2">
                    This programme is not just to help you avoid gaol: it is to help you get sober and clean and to help you change ALL areas of your life. You must be <strong>Honest, Open-Minded and Willing</strong> to be genuine and work hard.
                  </p>
                  <p className="text-xs text-blue-800 leading-relaxed mt-2">
                    It could be difficult in the early days, but little by little your life will improve if you stop using and/or drinking now.
                  </p>
                </div>
              )}
            </div>

            <div>
              <FieldLabel>Did you lose employment, your family, your wife/husband or children? Be honest: what actually happened that has made you want to stop using and drinking?</FieldLabel>
              <VoiceTextInput value={formData.crisis_what_made_you_stop} onChange={(v) => handleChange('crisis_what_made_you_stop', v)} placeholder="Be completely honest about what you've lost and what made you decide to change..." rows={8} testId="crisis-what-made-stop" />
            </div>

            <div className="border-t-2 border-blue-200 pt-4 mt-4">
              <div className="bg-blue-600 text-white rounded-lg p-3 mb-4">
                <p className="text-xs font-bold uppercase tracking-wider mb-1">Sobriety Clock Start</p>
                <p className="text-xs opacity-90">This date and time will automatically set your sobriety/clean clock on the dashboard.</p>
              </div>

              <FieldLabel requiredField="crisis_last_drink_date" formData={formData}>When was your last drink or drug?</FieldLabel>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Day of Week</label>
                  <Select value={formData.crisis_last_drink_day} onValueChange={(v) => handleChange('crisis_last_drink_day', v)}>
                    <SelectTrigger><SelectValue placeholder="Day" /></SelectTrigger>
                    <SelectContent>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Date</label>
                  <Input type="date" value={formData.crisis_last_drink_date} onChange={(e) => handleChange('crisis_last_drink_date', e.target.value)} data-testid="crisis-last-drink-date" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Time (approx)</label>
                  <Input type="time" value={formData.crisis_last_drink_time} onChange={(e) => handleChange('crisis_last_drink_time', e.target.value)} data-testid="crisis-last-drink-time" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                <strong className="text-blue-700">Your sobriety clock starts here.</strong> The date and time you enter become Day&nbsp;0&nbsp;Hour&nbsp;0 for your sober/clean clock.
                If you can't remember the exact time, leave it blank: we'll set it to <strong>midday</strong> in your local time so the clock starts ticking from a sensible point.
              </p>
            </div>
          </SectionBlock>

          {/* ===== 3. DRUG USE ===== */}
          <SectionBlock num={3} title="Drug Use History" sectionKey="drugUse" isOpen={expandedSections.drugUse} isIncomplete={incompleteKeys.has("drugUse")} onToggle={() => toggleSection("drugUse")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="primary_drug" formData={formData}>Primary Drug of Choice</FieldLabel>
              <Select value={formData.primary_drug} onValueChange={(v) => handleChange('primary_drug', v)}>
                <SelectTrigger><SelectValue placeholder="Select primary drug" /></SelectTrigger>
                <SelectContent>{PRIMARY_DRUGS.map(d => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Secondary Drug(s)</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                {PRIMARY_DRUGS.filter(d => d !== formData.primary_drug).map(d => (
                  <CheckboxOption key={d} label={d} checked={(formData.secondary_drugs || []).includes(d)} onChange={() => handleMultiSelect('secondary_drugs', d)} />
                ))}
              </div>
            </div>
            <div>
              <FieldLabel requiredField="method_of_use" formData={formData}>Method of Use</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                {METHODS_OF_USE.map(m => (
                  <CheckboxOption key={m} label={m} checked={(formData.method_of_use || []).includes(m)} onChange={() => handleMultiSelect('method_of_use', m)} />
                ))}
              </div>
            </div>
            <div>
              <FieldLabel requiredField="ever_injected" formData={formData}>Have you ever injected drugs?</FieldLabel>
              <YesNoSelect value={formData.ever_injected} onChange={(v) => handleChange('ever_injected', v)} />
            </div>
            {formData.ever_injected === 'yes' && (
              <div>
                <FieldLabel>Injected in the last 3 months?</FieldLabel>
                <YesNoSelect value={formData.injected_last_3_months} onChange={(v) => handleChange('injected_last_3_months', v)} />
              </div>
            )}
            <div>
              <FieldLabel>Additional Information</FieldLabel>
              <VoiceTextInput value={formData.additional_drug_info} onChange={(v) => handleChange('additional_drug_info', v)} placeholder="Any additional details about your drug/alcohol use..." testId="drug-additional" />
            </div>
          </SectionBlock>

          {/* ===== 4. LIVING SITUATION & DIET ===== */}
          <SectionBlock num={4} title="Living Situation & Diet" sectionKey="living" isOpen={expandedSections.living} isIncomplete={incompleteKeys.has("living")} onToggle={() => toggleSection("living")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="living_arrangement" formData={formData}>Current Living Arrangement</FieldLabel>
              <div className="grid grid-cols-1 gap-2">
                {['Homeless (includes couch surfing)', 'Alone', 'With family', 'With partner', 'With friends/roommates', 'Supported accommodation', 'Other'].map(arr => (
                  <CheckboxOption key={arr} label={arr} checked={formData.living_arrangement === arr} onChange={() => handleChange('living_arrangement', formData.living_arrangement === arr ? '' : arr)} />
                ))}
              </div>
            </div>
            {formData.living_arrangement?.includes('Homeless') && (
              <VoiceTextInput value={formData.homeless_details} onChange={(v) => handleChange('homeless_details', v)} placeholder="Describe your situation..." testId="homeless-details" />
            )}
            <div>
              <FieldLabel>Usual Accommodation</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                {['House', 'Unit/Apartment', 'Caravan', 'Boarding house', 'Other'].map(acc => (
                  <CheckboxOption key={acc} label={acc} checked={formData.usual_accommodation === acc} onChange={() => handleChange('usual_accommodation', formData.usual_accommodation === acc ? '' : acc)} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>How long have you been living here?</FieldLabel>
                <VoiceTextInput value={formData.living_how_long} onChange={(v) => handleChange('living_how_long', v)} placeholder="e.g., 6 months" rows={1} testId="living-how-long" />
              </div>
              <div>
                <FieldLabel>How long do you think you can stay?</FieldLabel>
                <VoiceTextInput value={formData.living_how_long_stay} onChange={(v) => handleChange('living_how_long_stay', v)} placeholder="e.g., indefinitely" rows={1} testId="living-how-long-stay" />
              </div>
            </div>

            {/* Diet Subsection */}
            <div className="border-t-2 border-blue-200 pt-4 mt-4">
              <h4 className="text-sm font-bold text-blue-700 uppercase tracking-wider mb-3">Diet & Nutrition</h4>
              <div>
                <FieldLabel>Are you vegetarian or vegan?</FieldLabel>
                <YesNoSelect value={formData.diet_vegetarian} onChange={(v) => handleChange('diet_vegetarian', v)} />
              </div>
              <div>
                <FieldLabel>Any important specific dietary needs?</FieldLabel>
                <VoiceTextInput value={formData.diet_specific_needs} onChange={(v) => handleChange('diet_specific_needs', v)} placeholder="Describe any special dietary requirements..." rows={2} testId="diet-needs" />
              </div>
              <div>
                <FieldLabel>Any food allergies (e.g., nuts, gluten)?</FieldLabel>
                <YesNoSelect value={formData.diet_food_allergies} onChange={(v) => handleChange('diet_food_allergies', v)} />
              </div>
              {formData.diet_food_allergies === 'yes' && (
                <>
                  <div>
                    <FieldLabel>What are you allergic to?</FieldLabel>
                    <VoiceTextInput value={formData.diet_allergy_details} onChange={(v) => handleChange('diet_allergy_details', v)} placeholder="List all food allergies..." rows={2} testId="diet-allergy-details" />
                  </div>
                  <div>
                    <FieldLabel>If you accidentally ingest one of these foods, what do you need to do?</FieldLabel>
                    <VoiceTextInput value={formData.diet_allergy_action} onChange={(v) => handleChange('diet_allergy_action', v)} placeholder="e.g., use EpiPen, take antihistamine, go to hospital..." rows={3} testId="diet-allergy-action" />
                  </div>
                </>
              )}
              <div>
                <FieldLabel>Do you eat regular meals (breakfast, lunch, dinner)?</FieldLabel>
                <YesNoSelect value={formData.diet_regular_meals} onChange={(v) => handleChange('diet_regular_meals', v)} />
              </div>
              <div>
                <FieldLabel>Are you diabetic?</FieldLabel>
                <YesNoSelect value={formData.diet_diabetic} onChange={(v) => handleChange('diet_diabetic', v)} />
              </div>
              {formData.diet_diabetic === 'yes' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Type</FieldLabel>
                    <Select value={formData.diet_diabetic_type} onValueChange={(v) => handleChange('diet_diabetic_type', v)}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="type1">Type 1</SelectItem>
                        <SelectItem value="type2">Type 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel>Management</FieldLabel>
                    <Select value={formData.diet_diabetic_management} onValueChange={(v) => handleChange('diet_diabetic_management', v)}>
                      <SelectTrigger><SelectValue placeholder="How managed?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="insulin">Insulin Dependent</SelectItem>
                        <SelectItem value="dietary">Dietary Controlled</SelectItem>
                        <SelectItem value="tablets">Tablets</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </SectionBlock>

          {/* ===== 5. INCOME & EMPLOYMENT ===== */}
          <SectionBlock num={5} title="Source of Income" sectionKey="income" isOpen={expandedSections.income} isIncomplete={incompleteKeys.has("income")} onToggle={() => toggleSection("income")} onSave={handleSubmit}>
            <div>
              <FieldLabel>Currently Employed?</FieldLabel>
              <YesNoSelect value={formData.is_employed} onChange={(v) => handleChange('is_employed', v)} />
            </div>
            {formData.is_employed === 'yes' && (
              <>
                <VoiceTextInput value={formData.employer} onChange={(v) => handleChange('employer', v)} placeholder="Employer/Company name..." rows={1} testId="employer" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Profession/Trade</FieldLabel>
                    <VoiceTextInput value={formData.profession} onChange={(v) => handleChange('profession', v)} placeholder="Your job title..." rows={1} testId="profession" />
                  </div>
                  <div>
                    <FieldLabel>Years in this Industry</FieldLabel>
                    <VoiceTextInput value={formData.years_in_industry} onChange={(v) => handleChange('years_in_industry', v)} placeholder="e.g., 10 years" rows={1} testId="years-industry" />
                  </div>
                </div>
                <div>
                  <FieldLabel>Industry</FieldLabel>
                  <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                    <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>{INDUSTRIES.map(ind => (<SelectItem key={ind} value={ind}>{ind}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel>How long employed here?</FieldLabel>
                  <VoiceTextInput value={formData.employment_duration} onChange={(v) => handleChange('employment_duration', v)} placeholder="e.g., 2 years" rows={1} testId="employment-duration" />
                </div>
              </>
            )}
            <div>
              <FieldLabel>Have you ever owned your own business?</FieldLabel>
              <YesNoSelect value={formData.has_own_business} onChange={(v) => handleChange('has_own_business', v)} />
            </div>
            {formData.has_own_business === 'yes' && (
              <>
                <VoiceTextInput value={formData.own_business_details} onChange={(v) => handleChange('own_business_details', v)} placeholder="Describe your business..." rows={2} testId="own-business" />
                <div>
                  <FieldLabel>Did you lose your business due to alcohol/drug issues?</FieldLabel>
                  <YesNoSelect value={formData.lost_business_due_to_addiction} onChange={(v) => handleChange('lost_business_due_to_addiction', v)} />
                </div>
              </>
            )}
            <div>
              <FieldLabel>Is your current or previous work environment dangerous to your Recovery?</FieldLabel>
              <p className="text-xs text-gray-500 mb-2">For example: working in a bar, restaurant or environment where alcohol/drugs are readily available</p>
              <YesNoSelect value={formData.work_environment_dangerous} onChange={(v) => handleChange('work_environment_dangerous', v)} />
            </div>
            {formData.work_environment_dangerous === 'yes' && (
              <VoiceTextInput value={formData.work_environment_details} onChange={(v) => handleChange('work_environment_details', v)} placeholder="Describe why your work environment is a risk to your Recovery..." rows={3} testId="work-env-details" />
            )}
            <div className="border-t pt-4 mt-4">
              <FieldLabel>Previously employed?</FieldLabel>
              <YesNoSelect value={formData.has_previous_employment} onChange={(v) => handleChange('has_previous_employment', v)} />
            </div>
            {formData.has_previous_employment === 'yes' && (
              <RepeatableEntries entries={formData.employment_history} label="Previous Employment" addLabel="Add Job"
                onAdd={() => addEntry('employment_history', { employer: '', profession: '', industry: '', duration: '', years_in_role: '' })}
                onUpdate={(idx, key, val) => updateEntry('employment_history', idx, key, val)}
                onRemove={(idx) => removeEntry('employment_history', idx)}
                renderEntry={(job, idx) => (
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Employer" value={job.employer || ''} onChange={(e) => updateEntry('employment_history', idx, 'employer', e.target.value)} />
                    <Input placeholder="Profession" value={job.profession || ''} onChange={(e) => updateEntry('employment_history', idx, 'profession', e.target.value)} />
                    <Input placeholder="Duration" value={job.duration || ''} onChange={(e) => updateEntry('employment_history', idx, 'duration', e.target.value)} />
                    <Input placeholder="Years in role" value={job.years_in_role || ''} onChange={(e) => updateEntry('employment_history', idx, 'years_in_role', e.target.value)} />
                  </div>
                )}
              />
            )}
            <div>
              <FieldLabel>Other sources of income</FieldLabel>
              <VoiceTextInput value={formData.income_details} onChange={(v) => handleChange('income_details', v)} placeholder="Benefits, pension, disability, family support..." rows={3} testId="income-details" />
            </div>
          </SectionBlock>

          {/* ===== 6. PREVIOUS TREATMENT ===== */}
          <SectionBlock num={6} title="Previous Treatment" sectionKey="treatment" isOpen={expandedSections.treatment} isIncomplete={incompleteKeys.has("treatment")} onToggle={() => toggleSection("treatment")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="has_previous_treatment" formData={formData}>Have you had previous treatment for alcohol/drug use?</FieldLabel>
              <YesNoSelect value={formData.has_previous_treatment} onChange={(v) => handleChange('has_previous_treatment', v)} />
            </div>
            {formData.has_previous_treatment === 'yes' && (
              <RepeatableEntries entries={formData.treatment_entries} label="Treatment History" addLabel="Add Treatment"
                colorClass="bg-blue-50 border-blue-200"
                onAdd={() => addEntry('treatment_entries', { type: '', type_other: '', location: '', when: '', duration: '', outcome: '' })}
                onUpdate={(idx, key, val) => updateEntry('treatment_entries', idx, key, val)}
                onRemove={(idx) => removeEntry('treatment_entries', idx)}
                renderEntry={(entry, idx) => (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={entry.type || ''} onValueChange={(v) => updateEntry('treatment_entries', idx, 'type', v)}>
                        <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>{TREATMENT_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input placeholder="Location/Facility" value={entry.location || ''} onChange={(e) => updateEntry('treatment_entries', idx, 'location', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="When (Month/Year)" value={entry.when || ''} onChange={(e) => updateEntry('treatment_entries', idx, 'when', e.target.value)} />
                      <Input placeholder="Duration" value={entry.duration || ''} onChange={(e) => updateEntry('treatment_entries', idx, 'duration', e.target.value)} />
                    </div>
                    <Input placeholder="Outcome / Notes" value={entry.outcome || ''} onChange={(e) => updateEntry('treatment_entries', idx, 'outcome', e.target.value)} />
                  </div>
                )}
              />
            )}
            <VoiceTextInput value={formData.additional_treatment_info} onChange={(v) => handleChange('additional_treatment_info', v)} placeholder="Any additional treatment information..." testId="treatment-additional" />
          </SectionBlock>

          {/* ===== 7. MENTAL HEALTH ===== */}
          <SectionBlock num={7} title="Mental Health" sectionKey="mentalHealth" isOpen={expandedSections.mentalHealth} isIncomplete={incompleteKeys.has("mentalHealth")} onToggle={() => toggleSection("mentalHealth")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="has_mental_health_diagnosis" formData={formData}>Do you have a mental health diagnosis?</FieldLabel>
              <YesNoSelect value={formData.has_mental_health_diagnosis} onChange={(v) => handleChange('has_mental_health_diagnosis', v)} />
            </div>
            {formData.has_mental_health_diagnosis === 'yes' && (
              <>
                <RepeatableEntries entries={formData.mh_admissions} label="Treatment/Admissions History" addLabel="Add Admission"
                  colorClass="bg-blue-50 border-blue-200"
                  onAdd={() => addEntry('mh_admissions', { type: '', location: '', when: '', duration: '', diagnosis: '' })}
                  onUpdate={(idx, key, val) => updateEntry('mh_admissions', idx, key, val)}
                  onRemove={(idx) => removeEntry('mh_admissions', idx)}
                  renderEntry={(entry, idx) => (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={entry.type || ''} onValueChange={(v) => updateEntry('mh_admissions', idx, 'type', v)}>
                          <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                          <SelectContent>{MH_TREATMENT_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                        </Select>
                        <Input placeholder="Location" value={entry.location || ''} onChange={(e) => updateEntry('mh_admissions', idx, 'location', e.target.value)} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="When" value={entry.when || ''} onChange={(e) => updateEntry('mh_admissions', idx, 'when', e.target.value)} />
                        <Input placeholder="Duration" value={entry.duration || ''} onChange={(e) => updateEntry('mh_admissions', idx, 'duration', e.target.value)} />
                      </div>
                      <Input placeholder="Diagnosis" value={entry.diagnosis || ''} onChange={(e) => updateEntry('mh_admissions', idx, 'diagnosis', e.target.value)} />
                    </div>
                  )}
                />
                <div>
                  <FieldLabel>Currently receiving treatment?</FieldLabel>
                  <YesNoSelect value={formData.currently_receiving_mh_treatment} onChange={(v) => handleChange('currently_receiving_mh_treatment', v)} />
                </div>
                {formData.currently_receiving_mh_treatment === 'yes' && (
                  <>
                    <VoiceTextInput value={formData.medication_details} onChange={(v) => handleChange('medication_details', v)} placeholder="Medication name, dosage, how taken..." rows={3} testId="medication-details" />
                    <VoiceTextInput value={formData.other_mh_treatments} onChange={(v) => handleChange('other_mh_treatments', v)} placeholder="Therapy, counseling, groups..." rows={2} testId="other-mh" />
                  </>
                )}
                <VoiceTextInput value={formData.additional_mh_info} onChange={(v) => handleChange('additional_mh_info', v)} placeholder="Any additional mental health information..." testId="mh-additional" />
              </>
            )}
          </SectionBlock>

          {/* ===== 8. HEALTH & FITNESS ===== */}
          <SectionBlock num={8} title="Health & Fitness" sectionKey="healthFitness" isOpen={expandedSections.healthFitness} isIncomplete={incompleteKeys.has("healthFitness")} onToggle={() => toggleSection("healthFitness")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="has_major_health_issues" formData={formData}>Do you have any major health issues?</FieldLabel>
              <YesNoSelect value={formData.has_major_health_issues} onChange={(v) => handleChange('has_major_health_issues', v)} />
            </div>
            {formData.has_major_health_issues === 'yes' && (
              <VoiceTextInput value={formData.health_issue_details} onChange={(v) => handleChange('health_issue_details', v)} placeholder="Describe your health issues..." rows={4} testId="health-issues" />
            )}
            <div>
              <FieldLabel>Are you allergic to any particular medication?</FieldLabel>
              <YesNoSelect value={formData.medication_allergies} onChange={(v) => handleChange('medication_allergies', v)} />
            </div>
            {formData.medication_allergies === 'yes' && (
              <VoiceTextInput value={formData.medication_allergy_details} onChange={(v) => handleChange('medication_allergy_details', v)} placeholder="List medication allergies and reactions..." rows={3} testId="med-allergy-details" />
            )}
            <div>
              <FieldLabel>Are you being treated for any major or chronic health issue other than alcoholism/addiction?</FieldLabel>
              <YesNoSelect value={formData.chronic_conditions} onChange={(v) => handleChange('chronic_conditions', v)} />
            </div>
            {formData.chronic_conditions === 'yes' && (
              <VoiceTextInput value={formData.chronic_condition_details} onChange={(v) => handleChange('chronic_condition_details', v)} placeholder="Describe chronic conditions and current treatment..." rows={4} testId="chronic-details" />
            )}
          </SectionBlock>

          {/* ===== 9. FRIENDS ===== */}
          <SectionBlock num={9} title="Friends" sectionKey="friends" isOpen={expandedSections.friends} isIncomplete={incompleteKeys.has("friends")} onToggle={() => toggleSection("friends")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="has_good_friends" formData={formData}>Do you have any good friends?</FieldLabel>
              <YesNoSelect value={formData.has_good_friends} onChange={(v) => handleChange('has_good_friends', v)} />
            </div>
            {formData.has_good_friends === 'yes' && (
              <>
                <div>
                  <FieldLabel>Do they use drugs or drink with you?</FieldLabel>
                  <YesNoSelect value={formData.friends_use_drugs} onChange={(v) => handleChange('friends_use_drugs', v)} />
                </div>
                {formData.friends_use_drugs === 'yes' && (
                  <div>
                    <FieldLabel>Do they use as much as you or more?</FieldLabel>
                    <div className="grid grid-cols-3 gap-2">
                      {['Less than me', 'About the same', 'More than me'].map(opt => (
                        <CheckboxOption key={opt} label={opt} checked={formData.friends_use_amount === opt} onChange={() => handleChange('friends_use_amount', formData.friends_use_amount === opt ? '' : opt)} />
                      ))}
                    </div>
                  </div>
                )}
                <VoiceTextInput value={formData.friends_details} onChange={(v) => handleChange('friends_details', v)} placeholder="Tell us about your friendships and social circle..." rows={4} testId="friends-details" />
              </>
            )}
            <div className="border-t pt-4 mt-4">
              <FieldLabel>Does anyone in your family or household use drugs or drink excessively?</FieldLabel>
              <YesNoSelect value={formData.family_drug_use} onChange={(v) => handleChange('family_drug_use', v)} />
            </div>
            {formData.family_drug_use === 'yes' && (
              <VoiceTextInput value={formData.family_drug_details} onChange={(v) => handleChange('family_drug_details', v)} placeholder="Describe the situation: who, what, how it affects you..." rows={4} testId="family-drug-details" />
            )}
          </SectionBlock>

          {/* ===== 10. LEGAL MATTERS ===== */}
          <SectionBlock num={10} title="Legal Matters" sectionKey="legal" isOpen={expandedSections.legal} isIncomplete={incompleteKeys.has("legal")} onToggle={() => toggleSection("legal")} onSave={handleSubmit}>
            <div>
              <FieldLabel requiredField="has_legal_matters" formData={formData}>Do you have any current legal matters?</FieldLabel>
              <YesNoSelect value={formData.has_legal_matters} onChange={(v) => handleChange('has_legal_matters', v)} />
            </div>
            {formData.has_legal_matters === 'yes' && (
              <>
                <div>
                  <FieldLabel>Type of Charge(s)</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {CHARGE_TYPES.map(t => (
                      <CheckboxOption key={t} label={t} checked={(formData.charge_types || []).includes(t)} onChange={() => handleMultiSelect('charge_types', t)} />
                    ))}
                  </div>
                </div>
                <VoiceTextInput value={formData.legal_location} onChange={(v) => handleChange('legal_location', v)} placeholder="Court name and jurisdiction..." rows={1} testId="legal-location" />
                <div>
                  <FieldLabel>Court date pending?</FieldLabel>
                  <YesNoSelect value={formData.court_date_pending} onChange={(v) => handleChange('court_date_pending', v)} />
                </div>
                <div>
                  <FieldLabel>Will AxiomRP assist court in sentencing?</FieldLabel>
                  <YesNoSelect value={formData.axiom_assist_court} onChange={(v) => handleChange('axiom_assist_court', v)} />
                </div>
              </>
            )}
            <div className="border-t pt-4 mt-4">
              <FieldLabel>Previous legal history?</FieldLabel>
              <YesNoSelect value={formData.previous_legal_history} onChange={(v) => handleChange('previous_legal_history', v)} />
            </div>
            {formData.previous_legal_history === 'yes' && (
              <RepeatableEntries entries={formData.previous_charges} label="Previous Charges" addLabel="Add Charge"
                colorClass="bg-red-50 border-red-200"
                onAdd={() => addEntry('previous_charges', { when: '', charge_type: '', drug_alcohol_related: '', details: '' })}
                onUpdate={(idx, key, val) => updateEntry('previous_charges', idx, key, val)}
                onRemove={(idx) => removeEntry('previous_charges', idx)}
                renderEntry={(charge, idx) => (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="When (Year)" value={charge.when || ''} onChange={(e) => updateEntry('previous_charges', idx, 'when', e.target.value)} />
                      <Select value={charge.charge_type || ''} onValueChange={(v) => updateEntry('previous_charges', idx, 'charge_type', v)}>
                        <SelectTrigger><SelectValue placeholder="Charge type" /></SelectTrigger>
                        <SelectContent>{CHARGE_TYPES.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <Input placeholder="Details" value={charge.details || ''} onChange={(e) => updateEntry('previous_charges', idx, 'details', e.target.value)} />
                  </div>
                )}
              />
            )}
            <VoiceTextInput value={formData.additional_legal_info} onChange={(v) => handleChange('additional_legal_info', v)} placeholder="Any additional legal information..." testId="legal-additional" />
          </SectionBlock>

          {/* ===== 11. PERSONAL PROFILE (STICKMAN) ===== */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <SectionHeader title="11. Your Personal Profile" section="stickman" isOpen={expandedSections.stickman} onClick={() => toggleSection('stickman')} />
            {expandedSections.stickman && (
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  This visualization summarizes your recorded information across all sections.
                </p>
                <StickmanVisualization data={formData} userName={user?.name || 'User'} size="normal" />
                <button
                  type="button"
                  onClick={() => toggleSection('stickman')}
                  className="w-full flex items-center justify-center gap-1.5 mt-4 py-2 text-xs font-semibold uppercase tracking-wide text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                  data-testid="section-collapse-stickman"
                >
                  <ChevronUp className="w-4 h-4" />
                  Collapse Section
                </button>
              </div>
            )}
          </div>

          {/* ===== 12. CASE MANAGEMENT PLAN ===== */}
          <CaseManagementPlanSection formData={formData} userName={user?.name || 'User'} />

          <Button onClick={() => handleSubmit('All Sections')} className="w-full bg-green-600 hover:bg-green-700 text-white py-4 text-lg font-bold" data-testid="submit-personal-history">
            Save All Sections
          </Button>
        </div>
      </main>

      <BackToTopButton />
      <Footer />
    </div>
  );
}

export default PersonalHistoryPage;
